import { Spacing } from '@persona-id/design-tokens';
import { CircleInfoIcon } from '@persona-id/design-tokens/icons';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import ReactSelect from 'dashboard/components/ReactSelect';
import Tooltip from 'dashboard/components/Tooltip';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import { filterUsableObjects, UsableObjectConfig } from 'dashboard/lib/workflows/objects';
import { WorkflowError, WorkflowFieldObjectKey } from 'dashboard/models/workflow';
import { WorkflowActionSetCaseSlaV1 } from 'dashboard/models/workflow-version';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import { assertNever } from 'lib/utils/types';

const SLA_TYPE_OPTIONS = ['days', 'minutes', 'hours'].map((unit) => {
  return { label: unit, value: unit as TimeUnit['type'] };
});

const SlaSettingWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: ${Spacing[2]};
`;

const StyledInput = styled(Input)`
  height: 100%;
`;
interface TimeUnit {
  time: number;
  type: 'minutes' | 'hours' | 'days';
}

const timeUnitToSeconds = ({ time, type }: TimeUnit) => {
  switch (type) {
    case 'minutes':
      return time * 60;
    case 'hours':
      return time * 3600;
    case 'days':
      return time * 86400;
    default:
      assertNever(type);
  }
};

/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
const secondsToTimeUnit = (seconds: number): TimeUnit => {
  if (seconds % 86400 === 0) {
    return {
      time: seconds / 86400,
      type: 'days',
    };
  } else if (seconds % 3600 === 0) {
    return {
      time: seconds / 3600,
      type: 'hours',
    };
  } else if (seconds % 60 === 0) {
    return {
      time: seconds / 60,
      type: 'minutes',
    };
  }
};

interface ParamsSetCaseSlaV1Props {
  token: string;
  editable: boolean;
  errors: WorkflowError[];
  usableObjects: UsableObjectConfig[];
  params: WorkflowActionSetCaseSlaV1['params'];
  focusStep: (token: string) => void;
  onMenuClose: () => void;
  updateActionParams: (newParams: WorkflowActionSetCaseSlaV1['params']) => void;
}

const ParamsSetCaseSlaV1 = ({
  token,
  editable,
  errors,
  usableObjects,
  params,
  focusStep,
  onMenuClose,
  updateActionParams,
}: ParamsSetCaseSlaV1Props) => {
  const cases = useMemo(() => {
    return filterUsableObjects({
      usableObjects,
      options: {
        permittedObjects: [WorkflowFieldObjectKey.Case],
        excludeNonPersonaObjects: true,
      },
    });
  }, [usableObjects]);
  // We want to keep time unit saved in state so the select values doesn't update while the user is changing the values
  const [timeUnit, setTimeUnit] = useState(secondsToTimeUnit(params['sla-seconds']));
  const selectCase = (kase: WorkflowActionSetCaseSlaV1['params']['case']) => {
    updateActionParams({
      ...params,
      case: kase,
    });
  };

  // Updates time if user views a different set case sla step
  useEffect(() => {
    setTimeUnit(secondsToTimeUnit(params['sla-seconds']));
  }, [token]);

  const updateSlaSeconds = (time: TimeUnit['time'], type: TimeUnit['type']) => {
    updateActionParams({
      ...params,
      'sla-seconds': timeUnitToSeconds({ time, type }),
    });
    setTimeUnit({ time, type });
  };

  const { hasErrorFor, renderErrorMessageFor } =
    useWorkflowError<keyof WorkflowActionSetCaseSlaV1['params']>(errors);

  return (
    <>
      <Spacer height={2} />
      <ParamLabel labelText='Case' htmlFor='params-set-sla__case-input' />
      <ReactSelect
        value={params.case}
        options={cases}
        onChange={selectCase}
        id='params-set-sla__case'
        disabled={!editable}
        hasError={hasErrorFor('case')}
        onFocusOption={focusStep}
        onMenuClose={onMenuClose}
      />
      {renderErrorMessageFor('case')}
      <Spacer height={2} />
      <FormControlLabel htmlFor='params-set-sla__sla-time'>
        SLA Target&nbsp;
        <Tooltip
          trigger='hover'
          tooltip={
            <Paragraph color='primary' size='sm'>
              SLA target is measured from when a case is created to when a case reaches an end
              state.
            </Paragraph>
          }
          hideArrow={true}
        >
          <CircleInfoIcon />
        </Tooltip>
      </FormControlLabel>
      <SlaSettingWrapper>
        <StyledInput
          value={timeUnit['time']}
          id='params-set-sla__sla-time'
          type='number'
          hasError={hasErrorFor('sla-seconds')}
          readOnly={!editable}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            updateSlaSeconds(Math.floor(Number(e.target.value)), timeUnit['type']);
          }}
          size='md'
        />
        <ReactSelect
          value={timeUnit['type']}
          options={SLA_TYPE_OPTIONS}
          onChange={(value) => {
            updateSlaSeconds(timeUnit['time'], value);
          }}
          id='params-set-sla__sla-type'
          disabled={!editable}
          hasError={hasErrorFor('case')}
          aria-label='SLA time unit'
        />
      </SlaSettingWrapper>
      {renderErrorMessageFor(['sla-seconds'])}
    </>
  );
};

export default ParamsSetCaseSlaV1;
